/**
 * The top-level component of the app.
 *
 * It consists of various sub-components and adds a Auth and Router wrapper.
 * Furthermore, the private routes are already considered here.
 */

import React from 'react';
import {BrowserRouter as Router, Redirect, Route, Switch} from 'react-router-dom'

import {FirebaseAuthProvider} from 'components/commons/firebase/FirebaseAuthProvider';
import AdminApp from 'components/admin/navigation/main/AdminApp';
import SignInSectionMain from 'components/commons/signin/manualSignIn/SignInSectionMain';
import PrivateRoute from 'components/commons/routing/routes/PrivateRoute';
import SignInSectionMoodle from "./components/commons/signin/moodleSignIn/SignInSectionMoodle";
import AuthorizationGate from "./components/commons/routing/authorizationGate/AuthorizationGate";
import GameApp from "./components/game/navigation/main/GameApp";
import SignInSectionLinking from "./components/commons/signin/accountLinking/SignInSectionLinking";
import {AppbarContentProvider} from "./components/commons/routing/appbar/AppbarContentProvider";
import {SnackbarProvider} from 'notistack';
import {Slide} from "@mui/material";
import RegistrationSection from "./components/commons/signup/registration/RegistrationSection";
import WaitingForEmailVerificationSection
    from "./components/commons/signup/emailVerification/WaitingForEmailVerificationSection";
import ProtectedRoute from "./components/commons/routing/routes/ProtectedRoute";
import {ConfirmProvider} from "material-ui-confirm";
import {IntlProvider} from 'react-intl'

import enCourses from './lang/course/en.json'
import enPlaylists from './lang/playlist/en.json'
import enStatistics from './lang/statistic/en.json'
import enUsers from './lang/user/en.json'
import enVideos from './lang/video/en.json'
import enScoredScenes from './lang/scoredScene/en.json'
import enSignIn from './lang/signIn/en.json'
import enNavigation from './lang/navigation/en.json'
import enDashboard from './lang/dashboard/en.json'
import enUtils from './lang/utils/en.json'
import enAbout from './lang/about/en.json'
import enChallengedDecisions from './lang/challengedDecisions/en.json'
import enGameButtons from "./lang_game/buttons/en.json";
import enGameCourses from "./lang_game/courses/en.json";
import enGameDashboard from "./lang_game/dashboard/en.json";
import enGamePlaylists from "./lang_game/playlists/en.json";
import enGameReview from "./lang_game/review/en.json";
import enGameSession from "./lang_game/session/en.json";
import enGameUtil from "./lang_game/util/en.json";


// TODO: Split like the english files as soon as everything is translated
import German from './lang/navigation/de.json'
import InitPassword from "./components/commons/signin/initPassword/InitPassword";

import {ThemeProvider, StyledEngineProvider, createTheme} from '@mui/material/styles';
import CssBaseline from "@mui/material/CssBaseline";

const theme = createTheme({
    palette: {
        mode: 'dark',
        //primary: {
        //    main: '#3f51b5'
        //},
        //secondary: {
        //    main: '#f50057',
        //},
        table: {
            primary: {
                header: {
                    backgroundColor: '#272727',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 'bold'
                },
                body: {
                    fontSize: 14
                }
            },
            secondary: {
                header: {
                    backgroundColor: 'transparent',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 'bold'
                },
                body: {
                    fontSize: 14
                }
            }
        }
    },
})

function App() {

    // Map, which is only used for easier determine the proper message file based on the the given "locale"
    const messages = {
        'de': German,
        'en': {
            ...enCourses,
            ...enPlaylists,
            ...enStatistics,
            ...enUsers,
            ...enVideos,
            ...enScoredScenes,
            ...enSignIn,
            ...enNavigation,
            ...enDashboard,
            ...enUtils,
            ...enAbout,
            ...enChallengedDecisions,
            ...enGameButtons,
            ...enGameDashboard,
            ...enGamePlaylists,
            ...enGameReview,
            ...enGameCourses,
            ...enGameUtil,
            ...enGameSession,
        }
    }

    // Determines the usage of the proper translation file and also country specific formats (e.g. date, currency)
    const locale = 'en'//navigator.language

    // Based on the locale try to find a proper translation file in the constructed "messages" map
    const localMessages = messages[locale] || messages['en']

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <IntlProvider messages={localMessages} locale={locale} defaultLocale="en">
                    <FirebaseAuthProvider>
                        <AppbarContentProvider>
                            <SnackbarProvider maxSnack={3} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                                              TransitionComponent={Slide}>
                                <ConfirmProvider>
                                    <CssBaseline/>
                                    <Router>
                                        <Switch>
                                            <ProtectedRoute path={'/waitingForEmailVerification'}
                                                            allowedRoles={['player', 'admin']}
                                                            component={WaitingForEmailVerificationSection}/>

                                            <Route path={'/'} exact component={SignInSectionMain}/>
                                            <Route path={'/moodleSignIn'} exact component={SignInSectionMoodle}/>
                                            <Route path={'/moodleSignInLinking'} exact
                                                   component={SignInSectionLinking}/>

                                            <Route path={'/signUp'} exact component={RegistrationSection}/>

                                            <Route path={'/initPassword'} component={InitPassword}/>

                                            <Route path={'/authorizedGate'} allowedRoles={['admin', 'player']}
                                                   component={AuthorizationGate}/>

                                            <PrivateRoute path={'/admin'}
                                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                                          component={AdminApp}/>
                                            <PrivateRoute path={'/game'} allowedRoles={['player']} component={GameApp}/>

                                            {/* Fallback URL handling - redirect to the sign-in-page, which will do the further handling */}
                                            <Redirect to={"/"}/>
                                        </Switch>
                                    </Router>
                                </ConfirmProvider>
                            </SnackbarProvider>
                        </AppbarContentProvider>
                    </FirebaseAuthProvider>
                </IntlProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

export default App;